/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { MessageBar, MessageBarType, Shimmer, Stack } from '@fluentui/react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExpandableCard } from 'src/core/components/ExpandableCard';
import { LuisSeparator } from 'src/core/components/LuisSeparator';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type QnaMakerAzureSearchCardProps = {
    isLoading: boolean;
};

const messages = defineMessages({
    successMessageBarText: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.successMessageBarText',
        defaultMessage: 'Your current resource, {resourceName}, can be used to create a custom question answering project'
    },
    warningMessageBarText: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.warningMessageBarText',
        defaultMessage:
            'We recommend that the Language resource and the Search instance are in the same location. You may still use this resource.'
    },
    languageResource: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.languageResource',
        defaultMessage: 'Language resource'
    },
    azureSearch: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.azureSearch',
        defaultMessage: 'Azure Search'
    },
    location: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.location',
        defaultMessage: 'Location:'
    },
    pricingTier: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.pricingTier',
        defaultMessage: 'Pricing tier:'
    },
    managedIdentity: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.managedIdentity',
        defaultMessage: 'Managed identity:'
    },
    enabled: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.enabled',
        defaultMessage: 'Enabled'
    },
    disabled: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.disabled',
        defaultMessage: 'Disabled'
    },
    azureSearchResource: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.azureSearchResource',
        defaultMessage: 'Azure Search resource:'
    },
    subscription: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.subscription',
        defaultMessage: 'Subscription:'
    },
    cardName: {
        id: 'qnaMaker.qnaMakerAzureSearchCard.cardName',
        defaultMessage: 'Qna maker azure search card.'
    }
});

const cardTheme = getScopedTheme('QnaMakerAzureSearchCard');

export const QnaMakerAzureSearchCard = observer((props: QnaMakerAzureSearchCardProps) => {
    const { isLoading } = props;
    const { isXLargeOrGreater } = useResponsiveMode();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const azureSession = rootStore.azureStore.session;

    const languageResource = azureSession.csAccountStore;

    const subscriptionName = rootStore.azureStore.subscriptionStoreHouse.items.find(sub => sub.id === azureSession.subscriptionId)?.name;

    const searchServiceName = azureSession.csAccountStore?.ownedSearchResourceId.split('/')[8];

    const searchService = rootStore.azureStore.searchServicesStoreHouse.items.find(service => service.name === searchServiceName);

    const CardHeader =
        languageResource?.region === searchService?.location ? (
            <MessageBar messageBarType={MessageBarType.success} styles={{ innerText: { width: '100%' } }}>
                {localize(messages.successMessageBarText, { resourceName: searchService?.name })}
            </MessageBar>
        ) : (
            <MessageBar messageBarType={MessageBarType.warning} styles={{ innerText: { width: '100%' } }}>
                {localize(messages.warningMessageBarText, { resourceName: searchService?.name })}
            </MessageBar>
        );

    const labelOnRender = (label: string, tooltipText?: string | React.ReactNode) => (
        <Stack horizontal verticalAlign="center">
            <LuisText styles={{ root: { color: cardTheme.labelColor, fontSize: '12px' } }}>{label}</LuisText>
            {tooltipText && (
                <Stack>
                    <CalloutButton content={tooltipText} />
                </Stack>
            )}
        </Stack>
    );

    const valueOnRender = (label: string) => (
        <LuisText styles={{ root: { fontSize: '12px' } }} fontWeight="bold">
            {label}
        </LuisText>
    );

    return (
        <>
            {isLoading ? (
                <>
                    <Shimmer />
                    <Shimmer width="75%" />
                    <Shimmer width="50%" />
                </>
            ) : (
                <ExpandableCard
                    isExpanded
                    isChevronHidden
                    minimizable={false}
                    onRenderHeader={CardHeader}
                    isSeparatorDisplayed={false}
                    cardName={localize(messages.cardName)}
                    styles={{ headerContainer: { cursor: 'default', padding: 0 }, root: { flex: 0 } }}>
                    <Stack
                        horizontal={isXLargeOrGreater}
                        tokens={{ childrenGap: isXLargeOrGreater ? 33 : 16, padding: !isXLargeOrGreater && '0 24px 0 0' }}
                        styles={{ root: { margin: '10px 0 10px 24px' } }}>
                        <Stack horizontal={isXLargeOrGreater} tokens={{ childrenGap: 16 }}>
                            <Stack tokens={{ childrenGap: 20 }}>
                                <LuisText fontWeight={500}>{localize(messages.languageResource)}</LuisText>
                                <Stack horizontal={isXLargeOrGreater} tokens={{ childrenGap: isXLargeOrGreater ? 100 : 50 }}>
                                    <Stack tokens={{ childrenGap: 15 }}>
                                        {labelOnRender(localize(messages.pricingTier))}
                                        {labelOnRender(localize(messages.managedIdentity))}
                                        {labelOnRender(localize(messages.location))}
                                    </Stack>
                                    <Stack tokens={{ childrenGap: 15 }}>
                                        {valueOnRender(`${languageResource?.sku.name} (${languageResource?.sku.code})`)}
                                        {valueOnRender(
                                            localize(languageResource?.isIdentityEnabled ? messages.enabled : messages.disabled)
                                        )}
                                        {valueOnRender(languageResource?.region)}
                                    </Stack>
                                </Stack>
                            </Stack>
                            <LuisSeparator vertical={isXLargeOrGreater} />
                            <Stack tokens={{ childrenGap: 20 }}>
                                <LuisText fontWeight={500}>{localize(messages.azureSearch)}</LuisText>
                                <Stack horizontal={isXLargeOrGreater} tokens={{ childrenGap: isXLargeOrGreater ? 100 : 50 }}>
                                    <Stack tokens={{ childrenGap: 15 }}>
                                        {labelOnRender(localize(messages.azureSearchResource))}
                                        {labelOnRender(localize(messages.subscription))}
                                        {labelOnRender(localize(messages.location))}
                                    </Stack>
                                    <Stack tokens={{ childrenGap: 15 }}>
                                        {valueOnRender(searchService?.name)}
                                        {valueOnRender(subscriptionName)}
                                        {valueOnRender(searchService?.location)}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </ExpandableCard>
            )}
        </>
    );
});
